import { Fade, Slide } from '@mui/material';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger } from 'src/components/ui/tabs';
import WidgetActivation from '../../Steps/components/WidgetActivation';
import WidgetCodeBlock from '../../Steps/components/WidgetCodeBlock';
import { ROOT } from 'src/utils/getWidgetCode';
import VideoInstructionCard from '../../Steps/components/VideoInstructionCard';
import SendInstructionCard from '../../Steps/components/SendInstructionCard';
import CustomiseDarkLightContainer from '../../CustomiseDarkLightContainer';
import CustomiseControlsContainer from '../../CustomiseControlsContainer';
import InfoTooltip from 'src/components/InfoTooltip';
import { TagSelector } from '../../CustomiseTagsContainer';
import { Link, TriangleAlert } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from 'src/components/ui/alert';
import { useNavigate } from 'react-router';
import { PATH_DASHBOARD } from 'src/routes/paths';
import useTransformations, { useHasTransformations } from 'src/hooks/useTransformations';

type WidgetSettingsTab = 'install' | 'customise';

export function getTransformationImageCardWidgetCode(workspaceId: string, widgetId: string) {
  return `<script defer src="${ROOT}/transformationimages/index.js"></script>
<div data-widget-transformationimages="${workspaceId}_${widgetId}"></div>`;
}

export default function TransformationImageCardSettings({
  workspaceId,
  widgetId,
}: {
  workspaceId: string;
  widgetId: string;
}) {
  const { t } = useTranslation();

  const [tab, setTab] = useState<WidgetSettingsTab>('install');
  const isInstallTab = tab === 'install';
  const isCustomiseTab = tab === 'customise';

  const code = getTransformationImageCardWidgetCode(workspaceId, widgetId);

  return (
    <>
      <TransformationImageCardAlert />
      {/* <Tabs
        value={tab}
        // className="w-[400px]"
        onValueChange={(value) => setTab(value as WidgetSettingsTab)}
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="install">{t('widgets.setup.tab.code')}</TabsTrigger>
          <TabsTrigger value="customise">{t('widgets.setup.tab.customise')}</TabsTrigger>
        </TabsList>
      </Tabs> */}
      <Fade key="slide-widget-code" in={isInstallTab}>
        <div className="pt-8" style={{ display: isInstallTab ? 'block' : 'none' }}>
          <div className="p-2 pb-8">
            <WidgetCodeBlock code={code} />
          </div>
          <div className="p-2">
            <VideoInstructionCard tutorialVideo="https://storage.googleapis.com/simply-review-b6fb2.appspot.com/tutorials/setup-video-widget-short.mov" />
          </div>
          <div className="p-2">
            <SendInstructionCard widgetId={widgetId} />
          </div>
          <div className="p-2 pt-8">
            <WidgetActivation widgetId={widgetId} />
          </div>
        </div>
      </Fade>
      {/* CUSTOMISE */}
      <Fade key="slide-widget-customise" in={isCustomiseTab}>
        <div className="pt-8" style={{ display: isCustomiseTab ? 'block' : 'none' }}>
          {/* <p className="">{t('widgets.setup.customise.headline')}</p> */}
          <CustomiseDarkLightContainer widgetId={widgetId} />

          <div className="pt-8">
            <div className="flex justify-between border-b-2 border-gray-300 pb-2">
              <p>{t('widgets.setup.customise.include')}</p>
              <p>
                {t('widgets.setup.customise.toggle')}
                <InfoTooltip size="small" title={t('widgets.setup.customise.toggleTooltip')} />
              </p>
            </div>
            <div className="pt-2">
              <CustomiseControlsContainer widgetId={widgetId} />
            </div>
            <div className="flex items-center justify-between pt-2">
              <div>
                <div className="text-sm">Highlight reviews by tags</div>
                <div className="text-xs text-muted-foreground">(No tags will display all)</div>
              </div>
              <div className="min-w-48">
                <TagSelector widgetId={widgetId} />
              </div>
            </div>
          </div>
        </div>
      </Fade>
    </>
  );
}

function TransformationImageCardAlert() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loading, hasTransformations } = useHasTransformations();
  if (loading) return null;
  if (hasTransformations) return null;
  return (
    <Slide direction="down" in={!hasTransformations} mountOnEnter unmountOnExit>
      <Alert
        className="cursor-pointer bg-orange-100 border-orange-200"
        onClick={() => navigate(PATH_DASHBOARD.general.transformations)}
      >
        <TriangleAlert className="h-5 w-5" />
        <AlertTitle>
          <b>{t('alert.feature.transformations.widget.headline')}</b>
        </AlertTitle>
        <AlertDescription>{t('alert.feature.transformations.widget.description')}</AlertDescription>
      </Alert>
    </Slide>
  );
}
